import React, { useContext, useEffect, useMemo, useReducer, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import firebase from 'firebase/compat/app';
import {
  ChevronDownIcon,
  ChevronUpDownIcon,
  ChevronUpIcon,
  EnvelopeIcon,
  PencilSquareIcon,
  TrashIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { firebaseAuth } from '../../../providers/AuthProvider';
import { db } from '../../../firebase';
import { userRoles, STAFF_ROLE } from '../../../Constants';

const inputCls =
  'mt-2 block w-full rounded-md border-0 bg-zinc-50 px-3 py-2 text-sm text-zinc-900 shadow-sm ring-1 ring-inset ring-zinc-300 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-zinc-900 dark:bg-zinc-800 dark:text-zinc-100 dark:ring-white/10 dark:focus:ring-white';

const reducer = (state, payload) => ({ ...state, ...payload });

const SortIndicator = ({ active, dir }) => {
  if (!active) return <ChevronUpDownIcon className="size-3 text-zinc-300 dark:text-zinc-600" aria-hidden="true" />;
  return dir === 'asc' ? (
    <ChevronUpIcon className="size-3" aria-hidden="true" />
  ) : (
    <ChevronDownIcon className="size-3" aria-hidden="true" />
  );
};

const sortValue = (u, key) => {
  if (key === 'email') return (u.email || '').toLowerCase();
  if (key === 'lastLogin') {
    const t = Date.parse(u.metadata?.lastSignInTime || '');
    return Number.isNaN(t) ? null : t;
  }
  if (key === 'created') {
    const t = Date.parse(u.metadata?.creationTime || '');
    return Number.isNaN(t) ? null : t;
  }
  if (key === 'lastRefresh') {
    const t = Date.parse(u.metadata?.lastRefreshTime || '');
    return Number.isNaN(t) ? null : t;
  }
  if (key === 'role') {
    const r = Number(u.customClaims?.role);
    return Number.isFinite(r) ? r : null;
  }
  if (key === 'verified') return u.emailVerified ? 1 : 0;
  if (key === 'mfa') return u.multiFactor?.enrolledFactors?.length || 0;
  if (key === 'events') {
    if ((u.email || '').indexOf('core12.com') > -1) return Number.POSITIVE_INFINITY;
    return u.assignedEvents?.length || 0;
  }
  return null;
};

const formatAuthDate = (value) => {
  if (!value) return '—';
  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) return String(value);
  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(parsed);
};

const formatBoolean = (value) => (value ? 'Yes' : 'No');

const valueOrDash = (value) => {
  if (value === true || value === false) return formatBoolean(value);
  if (value === undefined || value === null || value === '') return '—';
  return String(value);
};

const providerSummary = (providerData) => {
  const providers = providerData || [];
  if (!providers.length) return '—';
  return providers
    .map((provider) => provider.providerId || provider.email || provider.uid)
    .filter(Boolean)
    .join(', ') || '—';
};

const mfaSummary = (multiFactor) => {
  const factors = multiFactor?.enrolledFactors || [];
  if (!factors.length) return 'No';
  return factors
    .map((factor) => factor.displayName || factor.factorId || factor.phoneNumber || factor.uid)
    .filter(Boolean)
    .join(', ') || `${factors.length} enrolled`;
};

const userMetadataRows = (u) => [
  { label: 'UID', value: u.uid },
  { label: 'Email', value: u.email },
  { label: 'Email verified', value: formatBoolean(u.emailVerified) },
  { label: 'Disabled', value: formatBoolean(u.disabled) },
  { label: 'Created', value: formatAuthDate(u.metadata?.creationTime) },
  { label: 'Last login', value: formatAuthDate(u.metadata?.lastSignInTime) },
  { label: 'Last token refresh', value: formatAuthDate(u.metadata?.lastRefreshTime) },
  { label: 'Tokens valid after', value: formatAuthDate(u.tokensValidAfterTime) },
  { label: 'Display name', value: u.displayName },
  { label: 'Phone number', value: u.phoneNumber },
  { label: 'Photo URL', value: u.photoURL },
  { label: 'Tenant ID', value: u.tenantId },
  { label: 'Providers', value: providerSummary(u.providerData) },
  { label: 'MFA enrolled', value: mfaSummary(u.multiFactor) },
];

const Users = () => {
  const { user } = useContext(firebaseAuth);
  const navigate = useNavigate();
  const [data, setData] = useReducer(reducer, {
    users: [],
    invites: [],
    isFetching: false,
    message: null,
  });
  const [editUser, setEditUser] = useState({ customClaims: {}, profile: {} });
  const [sort, setSort] = useState({ key: null, dir: null });
  const [query, setQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const cycleSort = (key) => {
    setSort((prev) => {
      if (prev.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return { key: null, dir: null };
    });
  };

  const sortedUsers = useMemo(() => {
    const filtered = data.users.filter((entry) => {
      const matchesQuery = [entry.email, entry.displayName, entry.uid].some((value) =>
        String(value || '').toLowerCase().includes(query.trim().toLowerCase()));
      const matchesRole = roleFilter === 'all' || String(entry.customClaims?.role || '') === roleFilter;
      const matchesStatus = statusFilter === 'all' ||
        (statusFilter === 'disabled' ? entry.disabled : !entry.disabled);
      return matchesQuery && matchesRole && statusFilter !== 'invited' && matchesStatus;
    });
    if (!sort.key) return filtered;
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const va = sortValue(a, sort.key);
      const vb = sortValue(b, sort.key);
      if (va === null && vb === null) return 0;
      if (va === null) return 1;
      if (vb === null) return -1;
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }, [data.users, sort.key, sort.dir, query, roleFilter, statusFilter]);
  const visibleInvites = useMemo(() => data.invites.filter((entry) =>
    String(entry.data.email || '').toLowerCase().includes(query.trim().toLowerCase()) &&
    (roleFilter === 'all' || String(entry.data.role || '') === roleFilter) &&
    ['all', 'invited'].includes(statusFilter)), [data.invites, query, roleFilter, statusFilter]);

  const sortAriaLabel = (key, label) => {
    if (sort.key !== key) return `Sort by ${label}`;
    if (sort.dir === 'asc') return `Sorted by ${label} ascending; click to sort descending`;
    return `Sorted by ${label} descending; click to clear sort`;
  };

  const fetchInvites = async () => {
    try {
      const snap = await db.collection('invites').get();
      const invites = snap.docs.map((d) => ({ id: d.id, data: d.data() }));
      setData({ invites });
    } catch (err) {
      console.error('Error getting invites:', err);
    }
  };

  const fetchUsers = async () => {
    setData({ isFetching: true, message: null });
    try {
      const callable = firebase
        .functions()
        .httpsCallable('appV2/listUsersWithAssignments');
      const users = [];
      let pageToken;
      do {
        const result = await callable({ pageSize: 1000, pageToken });
        users.push(...(result?.data?.users || []));
        pageToken = result?.data?.nextPageToken || null;
      } while (pageToken);
      setData({ users, isFetching: false });
    } catch (e) {
      console.error('Failed to load users', e);
      setData({ isFetching: false, message: 'Could not load users.' });
    }
  };

  useEffect(() => {
    void fetchUsers();
    void fetchInvites();
  }, []);

  if (!user?.isAuthenticated || user?.role > 1) {
    if (user?.isAuthenticated && user?.role > 1) navigate('/manage/permissions');
    else navigate('/manage/sign-in');
  }

  const toggleEditUser = (uid, assignedEvents) => {
    setData({ message: null });
    if (editUser?.uid) {
      setEditUser({ customClaims: {}, profile: {} });
      return;
    }
    if (uid === 0 || !uid) {
      setEditUser({ customClaims: {}, profile: {} });
      void fetchUsers();
      return;
    }
    const getUser = firebase.functions().httpsCallable('appV2/getUser');
    getUser(uid).then((result) => {
      const userRef = db.collection('users').doc(result.data.uid);
      userRef.get().then((doc) => {
        const profile = doc.exists ? doc.data() : { userId: uid };
        setEditUser({ ...result.data, profile, assignedEvents });
      });
    });
  };

  const deleteUser = async () => {
    const currentUser = firebase.auth().currentUser;
    if (currentUser?.uid === editUser.uid) {
      setData({ message: 'The logged-in user cannot be deleted.' });
      return;
    }
    try {
      const callable = firebase.functions().httpsCallable('appV2/deleteUser');
      await callable(editUser.uid);
      setEditUser({ customClaims: {}, profile: {} });
      await db.collection('users').doc(editUser.uid).delete().catch(() => {});
      const inviteSnap = await db
        .collection('invites')
        .where('email', '==', editUser.email)
        .get();
      await Promise.all(inviteSnap.docs.map((d) => d.ref.delete()));
      void fetchUsers();
    } catch (e) {
      console.error(e);
    }
  };

  const setUserDisabled = async (uid, disabled) => {
    try {
      const callable = firebase.functions().httpsCallable('appV2/setUserDisabled');
      await callable({ uid, disabled });
      toast.success(disabled ? 'User disabled.' : 'User re-enabled.');
      setEditUser({ customClaims: {}, profile: {} });
      void fetchUsers();
    } catch (err) {
      console.error('setUserDisabled failed', err);
      toast.error('Could not update user status.');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const claims = { ...editUser.customClaims,
      crmManager: Number(editUser.customClaims?.role) !== STAFF_ROLE && editUser.customClaims?.crmManager === true };
    const setClaims = firebase.functions().httpsCallable('appV2/setClaims');
    setClaims({ uid: editUser.uid, claims })
      .then(async () => {
        await db.collection('users').doc(editUser.profile.userId).set({
          ...editUser.profile, crmManager: claims.crmManager,
        });
        setEditUser({ customClaims: {}, profile: {} });
        void fetchUsers();
      })
      .catch((error) => console.log('Error updating claims:', error));
  };

  const handleClaimsChange = (e) => {
    const role = e.target.name === 'role' ? Number(e.target.value) : Number(editUser.customClaims?.role);
    setEditUser({
      ...editUser,
      customClaims: { ...editUser.customClaims, [e.target.name]: e.target.value,
        ...(role === STAFF_ROLE ? { crmManager: false } : {}) },
    });
  };

  const sendInvite = async (email) => {
    try {
      const callable = firebase.functions().httpsCallable('appV2/resendInvite');
      await callable({ email });
      toast.success('Invitation email has been resent.');
      setData({ message: 'Invitation email has been resent.' });
      void fetchInvites();
    } catch (err) {
      console.error('resendInvite failed', err);
      toast.error('Could not resend invite. Please try again.');
    }
  };

  const deleteInvite = async (id, email) => {
    try {
      const callable = firebase.functions().httpsCallable('appV2/deleteInvite');
      await callable({ id: email || id });
      setData({ message: 'Invitation has been deleted.' });
      void fetchInvites();
    } catch (err) {
      console.error('deleteInvite failed', err);
      toast.error('Could not delete invite. Please try again.');
    }
  };

  const renderRoleLabel = (claims, role) => {
    let r = -1;
    if (claims?.role) r = claims.role - 1;
    if (role) r = role - 1;
    return r > -1 ? userRoles[r] : 'none';
  };

  /* Edit user view ---------------------------------------------------------- */
  if (editUser?.uid) {
    return (
      <div className="mx-auto max-w-3xl">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-100">
              Update user
            </h1>
            <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-300">{editUser.email}</p>
          </div>
        </div>

        <div className="mt-8 grid gap-6 md:grid-cols-2">
          <div>
            <label htmlFor="role" className="block text-sm font-medium text-zinc-900 dark:text-zinc-100">
              Role
            </label>
            <select
              id="role"
              name="role"
              className={inputCls}
              onChange={handleClaimsChange}
              value={editUser.customClaims?.role || ''}
            >
              <option value="" />
              {userRoles.map((label, index) => (
                <option key={label} value={index + 1}>
                  {label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <label className="mt-5 flex items-center gap-2 text-sm text-zinc-900 dark:text-zinc-100">
          <input type="checkbox" disabled={Number(editUser.customClaims?.role) === STAFF_ROLE}
            checked={Number(editUser.customClaims?.role) !== STAFF_ROLE && editUser.customClaims?.crmManager === true}
            onChange={(event) => setEditUser((current) => ({ ...current,
              customClaims: { ...current.customClaims, crmManager: event.target.checked } }))} />
          CRM Manager
        </label>

        <h2 className="mt-8 text-base font-semibold text-zinc-900 dark:text-zinc-100">Auth metadata</h2>
        <dl className="mt-3 grid grid-cols-1 gap-x-5 gap-y-3 rounded-lg bg-zinc-50 p-4 ring-1 ring-zinc-200 md:grid-cols-2 dark:bg-zinc-900 dark:ring-zinc-700">
          {userMetadataRows(editUser).map((row) => (
            <div key={row.label} className="min-w-0">
              <dt className="text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                {row.label}
              </dt>
              <dd className="mt-1 break-words text-sm text-zinc-900 dark:text-zinc-100">
                {valueOrDash(row.value)}
              </dd>
            </div>
          ))}
        </dl>

        <h2 className="mt-8 text-base font-semibold text-zinc-900 dark:text-zinc-100">Sign-in providers</h2>
        <div className="mt-3 overflow-hidden rounded-lg ring-1 ring-zinc-200 dark:ring-zinc-700">
          <table className="min-w-full divide-y divide-zinc-200 bg-zinc-50 dark:divide-zinc-700 dark:bg-zinc-900">
            <thead className="bg-zinc-100 dark:bg-zinc-800">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">Provider</th>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">Provider UID</th>
                <th className="hidden px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500 md:table-cell dark:text-zinc-400">Email</th>
                <th className="hidden px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500 md:table-cell dark:text-zinc-400">Phone</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
              {(editUser.providerData || []).map((provider, index) => (
                <tr key={`${provider.providerId || 'provider'}-${provider.uid || index}`}>
                  <td className="px-4 py-2 text-sm text-zinc-900 dark:text-zinc-100">{valueOrDash(provider.providerId)}</td>
                  <td className="px-4 py-2 text-sm text-zinc-600 dark:text-zinc-300">{valueOrDash(provider.uid)}</td>
                  <td className="hidden px-4 py-2 text-sm text-zinc-600 md:table-cell dark:text-zinc-300">{valueOrDash(provider.email)}</td>
                  <td className="hidden px-4 py-2 text-sm text-zinc-600 md:table-cell dark:text-zinc-300">{valueOrDash(provider.phoneNumber)}</td>
                </tr>
              ))}
              {!(editUser.providerData || []).length && (
                <tr>
                  <td colSpan={4} className="px-4 py-4 text-center text-sm text-zinc-500 dark:text-zinc-400">
                    No provider metadata.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <h2 className="mt-8 text-base font-semibold text-zinc-900 dark:text-zinc-100">MFA factors</h2>
        <ul className="mt-3 space-y-1 text-sm text-zinc-700 dark:text-zinc-300">
          {editUser.multiFactor?.enrolledFactors?.length ? (
            editUser.multiFactor.enrolledFactors.map((factor, index) => (
              <li key={factor.uid || index}>
                {valueOrDash(factor.displayName || factor.factorId || factor.phoneNumber || factor.uid)}
                {factor.enrollmentTime ? ` - enrolled ${formatAuthDate(factor.enrollmentTime)}` : ''}
              </li>
            ))
          ) : (
            <li className="text-zinc-500 dark:text-zinc-400">No MFA factors enrolled.</li>
          )}
        </ul>

        <h2 className="mt-8 text-base font-semibold text-zinc-900 dark:text-zinc-100">Assigned events</h2>
        <ul className="mt-3 space-y-1 text-sm text-zinc-700 dark:text-zinc-300">
          {editUser?.assignedEvents?.length ? (
            editUser.assignedEvents.map((e, i) => (
              <li key={i}>
                {e.sysname || e.name} — {e.startDate} — {e.status}
              </li>
            ))
          ) : (
            <li className="text-zinc-500 dark:text-zinc-400">No assigned events.</li>
          )}
        </ul>

        <div className="mt-8 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleSubmit}
              className="inline-flex items-center justify-center rounded-md bg-zinc-900 px-3 py-2 text-sm font-semibold text-zinc-50 shadow-sm hover:bg-zinc-800 dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-100"
            >
              Save
            </button>
            <button
              type="button"
              onClick={() => toggleEditUser(0)}
              className="inline-flex items-center justify-center rounded-md bg-zinc-50 px-3 py-2 text-sm font-semibold text-zinc-900 ring-1 ring-inset ring-zinc-300 hover:bg-zinc-100 dark:bg-zinc-800 dark:text-zinc-100 dark:ring-white/10 dark:hover:bg-zinc-700"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => setUserDisabled(editUser.uid, !editUser.disabled)}
              className="inline-flex items-center justify-center rounded-md bg-zinc-50 px-3 py-2 text-sm font-semibold text-zinc-900 ring-1 ring-inset ring-zinc-300 hover:bg-zinc-100 dark:bg-zinc-800 dark:text-zinc-100 dark:ring-white/10 dark:hover:bg-zinc-700"
            >
              {editUser.disabled ? 'Re-enable user' : 'Disable user'}
            </button>
          </div>
          <button
            type="button"
            onClick={() =>
              window.confirm('Are you sure to delete this record?') && deleteUser()
            }
            className="inline-flex items-center gap-1 text-sm font-medium text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
          >
            <TrashIcon className="size-4" /> Delete
          </button>
        </div>

        {data.message && (
          <div className="mt-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-inset ring-red-200 dark:bg-red-500/10 dark:text-red-300 dark:ring-red-500/20">
            {data.message}
          </div>
        )}
      </div>
    );
  }

  /* List view --------------------------------------------------------------- */
  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-100">Users</h1>
        <Link
          to="/manage/invite"
          className="inline-flex items-center gap-1 rounded-md bg-zinc-900 px-3 py-2 text-sm font-semibold text-zinc-50 shadow-sm hover:bg-zinc-800 dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-100"
        >
          <PlusIcon className="size-4" /> Invite user
        </Link>
      </div>

      {data.isFetching && (
        <div className="mt-4 text-sm text-zinc-500 dark:text-zinc-400">Loading users…</div>
      )}
      {data.message && (
        <div className="mt-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-inset ring-red-200 dark:bg-red-500/10 dark:text-red-300 dark:ring-red-500/20">
          {data.message}
        </div>
      )}

      <div className="mt-6 grid gap-3 rounded-lg bg-zinc-100 p-4 sm:grid-cols-3 dark:bg-zinc-900">
        <label className="text-sm font-medium">Search users and invitations
          <input type="search" className={inputCls} value={query}
            onChange={(event) => setQuery(event.target.value)} placeholder="Email, name, or ID" />
        </label>
        <label className="text-sm font-medium">Role
          <select className={inputCls} value={roleFilter} onChange={(event) => setRoleFilter(event.target.value)}>
            <option value="all">All roles</option>
            {userRoles.map((label, index) => <option key={label} value={String(index + 1)}>{label}</option>)}
          </select>
        </label>
        <label className="text-sm font-medium">Status
          <select className={inputCls} value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="disabled">Disabled</option>
            <option value="invited">Invited</option>
          </select>
        </label>
      </div>
      <p className="mt-3 text-sm text-zinc-600 dark:text-zinc-300">
        {sortedUsers.length} of {data.users.length} users · {visibleInvites.length} pending invitations shown
      </p>

      <div className="mt-6 overflow-x-auto rounded-lg ring-1 ring-zinc-200 dark:ring-zinc-700">
        <table className="min-w-[1200px] divide-y divide-zinc-200 bg-zinc-50 dark:divide-zinc-700 dark:bg-zinc-900">
          <thead className="bg-zinc-100 dark:bg-zinc-800">
            <tr>
              <th scope="col" className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                <button
                  type="button"
                  onClick={() => cycleSort('email')}
                  aria-label={sortAriaLabel('email', 'email')}
                  className="inline-flex items-center gap-1 uppercase tracking-wide hover:text-zinc-900 dark:hover:text-zinc-100"
                >
                  Email
                  <SortIndicator active={sort.key === 'email'} dir={sort.dir} />
                </button>
              </th>
              <th scope="col" className="hidden px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500 md:table-cell dark:text-zinc-400">
                <button
                  type="button"
                  onClick={() => cycleSort('lastLogin')}
                  aria-label={sortAriaLabel('lastLogin', 'last login')}
                  className="inline-flex items-center gap-1 uppercase tracking-wide hover:text-zinc-900 dark:hover:text-zinc-100"
                >
                  Last login
                  <SortIndicator active={sort.key === 'lastLogin'} dir={sort.dir} />
                </button>
              </th>
              <th scope="col" className="hidden px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500 lg:table-cell dark:text-zinc-400">
                <button
                  type="button"
                  onClick={() => cycleSort('created')}
                  aria-label={sortAriaLabel('created', 'created')}
                  className="inline-flex items-center gap-1 uppercase tracking-wide hover:text-zinc-900 dark:hover:text-zinc-100"
                >
                  Created
                  <SortIndicator active={sort.key === 'created'} dir={sort.dir} />
                </button>
              </th>
              <th scope="col" className="hidden px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500 xl:table-cell dark:text-zinc-400">
                <button
                  type="button"
                  onClick={() => cycleSort('lastRefresh')}
                  aria-label={sortAriaLabel('lastRefresh', 'last token refresh')}
                  className="inline-flex items-center gap-1 uppercase tracking-wide hover:text-zinc-900 dark:hover:text-zinc-100"
                >
                  Token refresh
                  <SortIndicator active={sort.key === 'lastRefresh'} dir={sort.dir} />
                </button>
              </th>
              <th scope="col" className="hidden px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500 lg:table-cell dark:text-zinc-400">
                <button
                  type="button"
                  onClick={() => cycleSort('verified')}
                  aria-label={sortAriaLabel('verified', 'email verified')}
                  className="inline-flex items-center gap-1 uppercase tracking-wide hover:text-zinc-900 dark:hover:text-zinc-100"
                >
                  Verified
                  <SortIndicator active={sort.key === 'verified'} dir={sort.dir} />
                </button>
              </th>
              <th scope="col" className="hidden px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500 lg:table-cell dark:text-zinc-400">
                <button
                  type="button"
                  onClick={() => cycleSort('mfa')}
                  aria-label={sortAriaLabel('mfa', 'MFA')}
                  className="inline-flex items-center gap-1 uppercase tracking-wide hover:text-zinc-900 dark:hover:text-zinc-100"
                >
                  MFA
                  <SortIndicator active={sort.key === 'mfa'} dir={sort.dir} />
                </button>
              </th>
              <th scope="col" className="hidden px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500 xl:table-cell dark:text-zinc-400">
                Providers
              </th>
              <th scope="col" className="hidden px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500 md:table-cell dark:text-zinc-400">
                <button
                  type="button"
                  onClick={() => cycleSort('role')}
                  aria-label={sortAriaLabel('role', 'role')}
                  className="inline-flex items-center gap-1 uppercase tracking-wide hover:text-zinc-900 dark:hover:text-zinc-100"
                >
                  Role
                  <SortIndicator active={sort.key === 'role'} dir={sort.dir} />
                </button>
              </th>
              <th scope="col" className="hidden px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500 md:table-cell dark:text-zinc-400">
                <button
                  type="button"
                  onClick={() => cycleSort('events')}
                  aria-label={sortAriaLabel('events', 'events')}
                  className="inline-flex items-center gap-1 uppercase tracking-wide hover:text-zinc-900 dark:hover:text-zinc-100"
                >
                  Events
                  <SortIndicator active={sort.key === 'events'} dir={sort.dir} />
                </button>
              </th>
              <th scope="col" className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                <span className="sr-only">Edit</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
            {sortedUsers.map((u) => (
              <tr
                key={u.uid}
                className="cursor-pointer hover:bg-zinc-100 dark:hover:bg-zinc-800"
                onClick={() => toggleEditUser(u.uid, u.assignedEvents)}
              >
                <td className="px-4 py-2 text-sm text-zinc-900 dark:text-zinc-100">
                  <div className="flex items-center gap-2">
                    {u.email}
                    {u.disabled && (
                      <span className="inline-flex items-center rounded bg-zinc-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-zinc-600 dark:bg-zinc-700 dark:text-zinc-300">
                        Disabled
                      </span>
                    )}
                  </div>
                </td>
                <td className="hidden px-4 py-2 text-sm text-zinc-600 md:table-cell dark:text-zinc-300">
                  {formatAuthDate(u.metadata?.lastSignInTime)}
                </td>
                <td className="hidden px-4 py-2 text-sm text-zinc-600 lg:table-cell dark:text-zinc-300">
                  {formatAuthDate(u.metadata?.creationTime)}
                </td>
                <td className="hidden px-4 py-2 text-sm text-zinc-600 xl:table-cell dark:text-zinc-300">
                  {formatAuthDate(u.metadata?.lastRefreshTime)}
                </td>
                <td className="hidden px-4 py-2 text-sm text-zinc-600 lg:table-cell dark:text-zinc-300">
                  {formatBoolean(u.emailVerified)}
                </td>
                <td className="hidden px-4 py-2 text-sm text-zinc-600 lg:table-cell dark:text-zinc-300">
                  {mfaSummary(u.multiFactor)}
                </td>
                <td className="hidden px-4 py-2 text-sm text-zinc-600 xl:table-cell dark:text-zinc-300">
                  {providerSummary(u.providerData)}
                </td>
                <td className="hidden px-4 py-2 text-sm text-zinc-600 md:table-cell dark:text-zinc-300">
                  {renderRoleLabel(u.customClaims)}
                </td>
                <td className="hidden px-4 py-2 text-sm text-zinc-600 md:table-cell dark:text-zinc-300">
                  {u.email?.indexOf('core12.com') > -1
                    ? 'All'
                    : u.assignedEvents?.length || 0}
                </td>
                <td className="px-4 py-2 text-right">
                  <button type="button" aria-label={`Edit ${u.email}`}
                    onClick={(event) => { event.stopPropagation(); toggleEditUser(u.uid, u.assignedEvents); }}
                    className="rounded p-1 text-zinc-500 hover:bg-zinc-200 hover:text-zinc-900 dark:hover:bg-zinc-700 dark:hover:text-zinc-100">
                    <PencilSquareIcon className="size-4" />
                  </button>
                </td>
              </tr>
            ))}
            {!data.isFetching && sortedUsers.length === 0 && (
              <tr>
                <td colSpan={10} className="px-4 py-6 text-center text-sm text-zinc-500 dark:text-zinc-400">
                  No users match these filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <h2 className="mt-10 text-base font-semibold text-zinc-900 dark:text-zinc-100">Pending invitations</h2>
      <div className="mt-3 overflow-hidden rounded-lg ring-1 ring-zinc-200 dark:ring-zinc-700">
        <table className="min-w-full divide-y divide-zinc-200 bg-zinc-50 dark:divide-zinc-700 dark:bg-zinc-900">
          <thead className="bg-zinc-100 dark:bg-zinc-800">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                Email
              </th>
              <th className="hidden px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500 md:table-cell dark:text-zinc-400">
                Role
              </th>
              <th className="hidden px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500 md:table-cell dark:text-zinc-400">
                Sent
              </th>
              <th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
            {visibleInvites.map((u) => (
              <tr key={u.id}>
                <td className="px-4 py-2 text-sm text-zinc-900 dark:text-zinc-100">{u.data.email}</td>
                <td className="hidden px-4 py-2 text-sm text-zinc-600 md:table-cell dark:text-zinc-300">
                  {renderRoleLabel(null, u.data.role)}
                </td>
                <td className="hidden px-4 py-2 text-sm text-zinc-600 md:table-cell dark:text-zinc-300">
                  {u.data.sentCount ? `${u.data.sentCount}×` : '—'}
                </td>
                <td className="px-4 py-2 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => sendInvite(u.data.email)}
                      className="text-zinc-400 hover:text-zinc-900 dark:text-zinc-500 dark:hover:text-zinc-100"
                      title="Resend invite"
                    >
                      <EnvelopeIcon className="size-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        window.confirm('Are you sure to delete this record?') &&
                        deleteInvite(u.id, u.data.email)
                      }
                      className="text-zinc-400 hover:text-red-600 dark:text-zinc-500 dark:hover:text-red-400"
                      title="Delete invite"
                    >
                      <TrashIcon className="size-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {visibleInvites.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-center text-sm text-zinc-500 dark:text-zinc-400">
                  No pending invitations match these filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Users;
